import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import { ModGroupRowModel } from '../util/modGroupRow.utils';
import TruncationTooltip from './TruncationTooltip';

/**
 * The pane's right gutter. Matches `ModGroup.modGroupClass`'s 12px left indent
 * so the group rows and the option drawer sit inside the same margins on both
 * sides. Shared with `OptionDrawer`, which is the other half of the block.
 */
export const MOD_GROUP_ROW_GUTTER_PX = 12;

const useStyles = makeStyles<{ isUnmet: boolean; isOpen: boolean }>()(
  (_theme, { isUnmet, isOpen }) => ({
    row: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 8,
      width: '100%',
      minWidth: 0,
      // HITL-1619: a right gutter matching the block's own left indent
      // (`ModGroup.modGroupClass`, `marginLeft: spacing(1.5)`). The value cell
      // is right-aligned and ellipsising, so with none it ended flush against
      // the card's edge — and a truncated value ran its ellipsis straight into
      // the border. Only the LEFT was ever paid for, by that margin.
      padding: `3px ${MOD_GROUP_ROW_GUTTER_PX}px 3px 0`,
      cursor: 'pointer',
      backgroundColor: isOpen ? '#EFF6FB' : 'transparent',
    },
    // The key never truncates before the value does: it is sized to its own
    // content and refuses to shrink, so the value cell absorbs every loss.
    key: {
      flexShrink: 0,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: isUnmet ? Colors.Thunderbird : Colors.silverChalice,
    },
    // Takes the remaining space and ellipsises. One line, always.
    value: {
      flex: 1,
      minWidth: 0,
      textAlign: 'right',
      fontSize: '13px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: isUnmet ? Colors.Thunderbird : Colors.daintree,
    },
    // `flex-shrink: 0`, so the names lose characters and the count never lies
    // about how many there are.
    countChip: {
      flexShrink: 0,
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: '14px',
      padding: '0 4px',
      borderRadius: 7,
      backgroundColor: Colors.alto,
      color: Colors.daintree,
    },
    caret: {
      flexShrink: 0,
      fontSize: '10px',
      color: Colors.silverChalice,
      transform: isOpen ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      display: 'inline-block',
    },
  })
);

interface ModGroupRowProps {
  model: ModGroupRowModel;
  isOpen: boolean;
  onToggle: () => void;
  /** Indent depth; 12px per level, matching `ModGroup.modGroupClass`. */
  depth?: number;
}

export const MOD_GROUP_ROW_INDENT_PX = 12;

/**
 * HITL-1616 — one modifier group, one line.
 *
 * Replaces a wall of chips whose labels were vowel-stripped to fit
 * (`getModGroupItemLabel` → `novowelWithoutStitute`, which renders "Substitute
 * Sugar Free Vanilla" as `Sbsttt Sgr Fr Vnll`). The row shows the group's name
 * and what is actually chosen, at full spelling, and defers the options to the
 * drawer.
 *
 * The row is always rendered — it is the drawer below it that hides — because
 * the group's `ModGroup` must stay mounted for its default-selection effect.
 */
const ModGroupRow = ({
  model,
  isOpen,
  onToggle,
  depth = 0,
}: ModGroupRowProps) => {
  const { classes } = useStyles({ isUnmet: model.isUnmet, isOpen });
  const { label, value, selectedNames, selectedCount, isUnmet, unmetText } =
    model;

  // The one place the full set is guaranteed readable, one name per line.
  const valueTooltip = selectedNames.length ? (
    <span style={{ whiteSpace: 'pre-line' }}>{selectedNames.join('\n')}</span>
  ) : (
    ''
  );

  return (
    <div
      className={classes.row}
      style={
        depth ? { paddingLeft: depth * MOD_GROUP_ROW_INDENT_PX } : undefined
      }
      onClick={onToggle}
      data-testid="mod-group-row"
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onToggle();
      }}
    >
      <span className={classes.caret}>▸</span>
      <TruncationTooltip title={label}>
        <span className={classes.key} data-testid="mod-group-row-key">
          {isUnmet ? `! ${label}` : label}
        </span>
      </TruncationTooltip>
      <TruncationTooltip title={valueTooltip}>
        <Typography
          component="span"
          className={classes.value}
          data-testid="mod-group-row-value"
        >
          {isUnmet && !value ? unmetText : value}
        </Typography>
      </TruncationTooltip>
      {selectedCount > 1 && (
        <span className={classes.countChip} data-testid="mod-group-row-count">
          ×{selectedCount}
        </span>
      )}
    </div>
  );
};

export default ModGroupRow;
